import {
    CompoundEntityRef,
    Entity,
    parseEntityRef,
} from '@backstage/catalog-model';

export const ANNOTATION_CMDB_UID = 'schema.dp.netrtl.com/uid';

/**
 * The team that operates the infrastructure a product runs on - who to turn to
 * for it, as opposed to the owner, who builds the product.
 *
 * The CMDB names it per product in `management.platform_team`, as a team vuid,
 * and the provider puts that on the entity as `spec.platformTeam`. A team vuid
 * is what a team entity is named after, so the string addresses the group as
 * it stands; nothing has to be looked up. A product from a v11 artifact has
 * none, hence the undefined.
 */
export function platformTeamRef(entity: Entity): CompoundEntityRef | undefined {
    const name = entity.spec?.platformTeam;
    if (typeof name !== 'string' || !name) {
        return undefined;
    }
    return { kind: 'group', name: name, namespace: 'default' };
}

/**
 * The CMDB uid of a team or a product. The entity is named after its vuid,
 * while the model api and the directories of the CMDB repository go by the
 * uid, so it is read from the annotation instead of from the entity name.
 * An entity the provider has not touched since the rename still carries the
 * uid as its name, hence the fallback.
 */
export function cmdbUidOf(entity: Entity): string {
    return (
        entity.metadata.annotations?.[ANNOTATION_CMDB_UID] ??
        entity.metadata.name
    );
}

/** How the `spec.updated` of an entity is shown, see {@link formatUpdated}. */
export type UpdatedDisplay = {
    /** Short and relative, e.g. `3 days ago`. */
    label: string;
    /** The exact moment behind the label, absent if there is none to show. */
    title?: string;
};

/** Largest first, the one that fits the distance is the one that is used. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 60 * 60 * 1000],
    ['month', 30 * 24 * 60 * 60 * 1000],
    ['week', 7 * 24 * 60 * 60 * 1000],
    ['day', 24 * 60 * 60 * 1000],
    ['hour', 60 * 60 * 1000],
    ['minute', 60 * 1000],
];

/**
 * The `spec.updated` of an entity as something to read: how long ago it was,
 * with the exact moment kept for the tooltip.
 *
 * What the field is really asked is whether the data is fresh, which `3 days
 * ago` answers at a glance and a timestamp does not.
 *
 * The providers store the value as UTC in ISO 8601, which is what the backend
 * logs print and what keeps it stable no matter which pod wrote it. Only the
 * display is local, and it is pinned to Europe/Berlin rather than left to the
 * browser, so that two people looking at the same entity read the same time.
 *
 * Anything that does not parse as a date is handed back as it stands - an
 * entity untouched since before the switch to ISO still carries a locale
 * string, and showing it beats showing `Invalid Date`.
 */
export function formatUpdated(
    updated: string | undefined,
    now: Date = new Date(),
): UpdatedDisplay {
    if (!updated) {
        return { label: 'undefined' };
    }
    const parsed = Date.parse(updated);
    if (Number.isNaN(parsed)) {
        return { label: updated };
    }
    return {
        label: relativeTo(parsed, now.getTime()),
        title: new Date(parsed).toLocaleString('en-GB', {
            timeZone: 'Europe/Berlin',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        }),
    };
}

function relativeTo(then: number, now: number): string {
    const distance = then - now;
    // `auto` is what turns -1 day into `yesterday` rather than `1 day ago`.
    const format = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
    for (const [unit, milliseconds] of RELATIVE_UNITS) {
        if (Math.abs(distance) >= milliseconds) {
            return format.format(Math.round(distance / milliseconds), unit);
        }
    }
    return 'just now';
}

export function hasProducts(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'product');
}

export function hasServices(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'service');
}

export function hasSystems(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'system');
}

export function hasComponents(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'component');
}

export function hasApis(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'api');
}

export function hasResources(entity: Entity): boolean {
    return hasRelationOfKind(entity, 'resource');
}

/** A Kubernetes cluster of the entity, or a slice of one. */
export function hasKubernetes(entity: Entity): boolean {
    return hasClusters(entity) || hasClusterSlices(entity);
}

export function hasClusters(entity: Entity): boolean {
    return hasResourceNamedLike(entity, 'k8s-');
}

export function hasClusterSlices(entity: Entity): boolean {
    return hasResourceNamedLike(entity, 'k8ss-');
}

/**
 * A cloud account of the product - a GCP project, an AWS account, an Azure
 * subscription or an OCI account - in any of its environments.
 *
 * Unlike a cluster, an account cannot be told from the relations: it is named
 * after its provider id, not after a vuid that starts with its kind. The
 * catalog backend therefore lists the account types of every environment on
 * the product entity itself.
 */
export function hasCloudAccounts(entity: Entity): boolean {
    const environments = (entity.spec?.data as any)?.environments ?? {};
    return Object.values(environments).some(
        (environment: any) => environment?.cloudAccounts?.length > 0,
    );
}

/** What the id of a cloud account is called at the vendor, per resource type. */
const ACCOUNT_ID_LABELS: Record<string, string | undefined> = {
    'gcp-project': 'GCP Project ID',
    'aws-account': 'AWS Account ID',
    'azure-subscription': 'Azure Subscription ID',
    'oci-account': 'OCI Compartment OCID',
};

const PROJECT_NUMBER_WARNING =
    'Do not use the project number to refer to this project - in configuration, code, permissions or anywhere else. Use the GCP Project ID instead. The number is only shown so that you can look up a project GCP mentions by its number.';

/**
 * The ids the vendor knows a cloud account by, labelled the way the vendor
 * calls them. The catalog backend puts the id under `spec.accountId` for every
 * cloud, and a GCP project carries its project number as well, as far as the
 * asset inventory has seen it. Anything else has none.
 *
 * The project number comes with a warning: it is shown because GCP puts it in
 * front of people - in error messages, service agent addresses - and they come
 * here to find out which project it is, not because anything should be built
 * on it. Whatever refers to a project uses the project id.
 */
export function cloudAccountIdsOf(
    entity: Entity,
): { label: string; value: string; description?: string }[] {
    const label = ACCOUNT_ID_LABELS[entity.spec?.type as string];
    if (entity.kind.toLocaleLowerCase('en-US') !== 'resource' || !label) {
        return [];
    }
    const { accountId, projectNumber } = entity.spec as {
        accountId?: string;
        projectNumber?: string;
    };
    return [
        ...(accountId ? [{ label: label, value: accountId }] : []),
        ...(projectNumber
            ? [
                  {
                      label: 'GCP Project Number',
                      value: projectNumber,
                      description: PROJECT_NUMBER_WARNING,
                  },
              ]
            : []),
    ];
}

/**
 * Whether a resource of the entity is named the way the CMDB names one kind of
 * thing.
 *
 * A relation carries nothing but the ref of what it points at, so the
 * `spec.type` of the other entity cannot be read here - the vuid can, because
 * an entity is named after it and every vuid starts with the kind it is of:
 * `k8s-` a cluster, `k8ss-` a slice. Note that the one is not a prefix of the
 * other, the separator differs.
 */
function hasResourceNamedLike(entity: Entity, prefix: string): boolean {
    return (
        entity.relations?.some(it => {
            const ref = parseEntityRef(it.targetRef);
            return ref.kind === 'resource' && ref.name.startsWith(prefix);
        }) === true
    );
}

export function hasLinks(entity: Entity): boolean {
    return Boolean(entity.metadata.links && entity.metadata.links?.length > 0);
}

/**
 * Whether the entity says anywhere it is reached - an address, a Jira project.
 * The provider leaves `contactChannels` off where there is none of it, see
 * ContactChannelsCard.
 */
export function hasContactChannels(entity: Entity): boolean {
    const contactChannels = entity.spec?.contactChannels as
        | Record<string, unknown[]>
        | undefined;
    return Object.values(contactChannels ?? {}).some(it => it?.length > 0);
}

/**
 * Whether the entity states an Entra group it asked for itself.
 *
 * Only `custom` is looked at, which is all a product has - and the product page
 * is what asks: unlike a team or a department, which have the technical pair
 * whether they declare it or not, almost every product has no group at all, and
 * a Groups tab there would be an empty page on nearly all of them.
 */
export function hasCustomGroups(entity: Entity): boolean {
    const custom = (entity.spec?.cmdbGroups as { custom?: unknown[] })?.custom;
    return (custom?.length ?? 0) > 0;
}

/**
 * Whether the entity is anything but an Entra group, as a relations graph
 * filters its nodes. A team and a department hang their groups off `children`,
 * but who is in them is the Groups tab's business - in the graph they are a
 * row of leaves that crowds out the org tree and what the entity owns.
 */
export function isNotAdGroup(entity: Entity): boolean {
    return entity.spec?.type !== 'ad-group';
}

export function hasOwner(entity: Entity): boolean {
    return Boolean(entity.spec?.owner && entity.spec.owner !== 'undefined');
}

export function hasTechnicalContacts(entity: Entity): boolean {
    return Boolean(
        entity?.spec?.technicalContacts &&
            (entity.spec.technicalContacts as any[]).length > 0,
    );
}

export function notHasOwner(entity: Entity): boolean {
    return !hasOwner(entity);
}

function hasRelationOfKind(entity: Entity, kind: string): boolean {
    return (
        entity.relations?.find(
            it => parseEntityRef(it.targetRef).kind === kind,
        ) != undefined
    );
}
